import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { adminApi } from '@/api/client';
import LoadingSpinner from '@/components/common/LoadingSpinner';

interface Setting {
  id: string;
  key: string;
  value: unknown;
  createdAt: string;
  updatedAt: string;
}

export default function SettingsPage() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<Setting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await adminApi.getSettings();
      setSettings(response.data);
    } catch (err) {
      console.error('Error fetching settings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (setting: Setting) => {
    setEditingKey(setting.key);
    setEditValue(JSON.stringify(setting.value, null, 2));
  };

  const handleSave = async (key: string) => {
    setIsSaving(true);

    try {
      let parsedValue: unknown;
      try {
        parsedValue = JSON.parse(editValue);
      } catch {
        parsedValue = editValue;
      }

      await adminApi.updateSetting(key, parsedValue);
      setEditingKey(null);
      fetchSettings();
    } catch (err) {
      console.error('Error saving setting:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKey.trim()) return;
    setIsSaving(true);

    try {
      let parsedValue: unknown;
      try {
        parsedValue = JSON.parse(newValue);
      } catch {
        parsedValue = newValue;
      }

      await adminApi.updateSetting(newKey, parsedValue);
      setNewKey('');
      setNewValue('');
      fetchSettings();
    } catch (err) {
      console.error('Error creating setting:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
      </div>

      {/* Add New Setting */}
      <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Add New Setting</h3>
        <form onSubmit={handleCreate} className="flex flex-col sm:flex-row gap-3 sm:gap-4">
          <input
            type="text"
            value={newKey}
            onChange={(e) => setNewKey(e.target.value)}
            placeholder="Setting key"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
          />
          <input
            type="text"
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            placeholder="Value (JSON or string)"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg"
          />
          <button
            type="submit"
            disabled={isSaving || !newKey.trim()}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 whitespace-nowrap"
          >
            Add
          </button>
        </form>
      </div>

      {/* Settings List */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/4">
                  {t('settings.key')}
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {t('settings.value')}
                </th>
                <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {settings.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-3 sm:px-6 py-8 text-center text-gray-500">
                    No settings found. Add a new setting above.
                  </td>
                </tr>
              ) : (
                settings.map((setting) => (
                  <tr key={setting.id}>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {setting.key}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 text-sm text-gray-900">
                      {editingKey === setting.key ? (
                        <textarea
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                          rows={4}
                        />
                      ) : (
                        <pre className="bg-gray-50 px-3 py-2 rounded text-sm overflow-x-auto max-w-xs sm:max-w-lg">
                          {JSON.stringify(setting.value, null, 2)}
                        </pre>
                      )}
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-right text-sm">
                      {editingKey === setting.key ? (
                        <div className="flex flex-col sm:flex-row justify-end gap-2">
                          <button
                            onClick={() => setEditingKey(null)}
                            className="text-gray-500 hover:text-gray-700"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => handleSave(setting.key)}
                            disabled={isSaving}
                            className="text-primary hover:text-primary-dark disabled:opacity-50"
                          >
                            {isSaving ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleEdit(setting)}
                          className="text-primary hover:text-primary-dark"
                        >
                          {t('common.edit')}
                        </button>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
